import { Entity } from '@api/api/entity-schema';
import { Resolution } from '@api/api/price-schema';
import { SnakeMeta } from '@api/api/timeseries-schema';
import {
  CustomMixDomain,
  isIntradayResample,
  RangeHorizon,
  ResampleIntervals,
  SeriesType,
  TimeSeriesItem,
} from '@chart';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { useQueryClient } from '@tanstack/react-query';
import { Tracking } from '@terminal/services/tracking';
import { useChartLoad } from '@terminal/views/turbo-chart/hooks/use-chart-load/useChartLoad';
import { ChartSearchType } from '@terminal/views/turbo-chart/types/search.types';
import { useMemo, useState } from 'react';

import {
  ChartSettings,
  getValidHorizon,
} from '../../utils/chart-settings-utils/chart-settings-utils';
import { isChartSinglePriceEntity } from '../../utils/chart-theme-asset/chartThemeAsset';
import {
  createChartDataWithChangedAsset,
  getHorizons,
  getNewLineColorToken,
  getTargetPaneIndex,
  getTerminalPrimaryDetails,
  getUpdatedPaneData,
  updateAllPanes,
  updateChartPane,
  updatePane,
} from '../../utils/chart-utils/chart-utils';
import type { TerminalPaneData } from '../../utils/chart-utils/types';
import {
  getAssetResample,
  getChartDataEmptyResamples,
  getResampleAfterDailyFallback,
  mergeIntradayEmptyResamples,
} from '../../utils/resample-utils/resample-utils';
import { useChartErrors } from '../use-chart-errors/useChartErrors';
import { useChartPane } from '../use-chart-pane/useChartPane';
import { useChartSettings } from '../use-chart-settings/useChartSettings';
import { ChartState, useChartState } from '../use-chart-state/useChartState';
import { ThemeVariant, useChartTheme } from '../use-chart-theme/useChartTheme';
import { useResample } from '../use-resample/useResample';
import { createChartTimeSeriesQueryOptions } from './useTurboChart.utils';

export interface GetTimeSeriesProps {
  latestAsset: Entity;
  resolution: Resolution;
  /**
   * Called when an intraday request returned no data and we fell back to
   * daily prices. Lets callers know they should adjust horizon/resample to
   * match the data they actually got.
   */
  onFallbackToDaily?: () => void;
}

export interface OnChartDataReadyProps {
  chartPanes: TerminalPaneData[];
  emptyResamples: ResampleIntervals[];
  validResample: ResampleIntervals;
  validHorizon: RangeHorizon | undefined;
  domain: CustomMixDomain | null;
  hidden: string[] | undefined;
  dailyTimeseries: TimeSeriesItem[];
}

export interface UseTurboChartProps {
  openSearch?: (
    type: ChartSearchType,
    {
      changingAssets,
      paneIndex,
    }: { changingAssets?: Entity; paneIndex?: number }
  ) => void;
  initialChartSettings?: ChartSettings;
  initialChartState?: ChartState;
  restoreLastUsedChart?: boolean;
  shouldUseSearchParams?: boolean;
  variants?: ThemeVariant[];
}

export type UseTurboChartReturn = ReturnType<typeof useTurboChart>;

export const useTurboChart = ({
  openSearch,
  initialChartSettings,
  initialChartState,
  restoreLastUsedChart,
  shouldUseSearchParams = true,
  variants = [],
}: UseTurboChartProps) => {
  const { colors } = useChartTheme(variants);
  const queryClient = useQueryClient();
  const {
    chartState,
    updateChartState,
    resetChartState,
    primaryAsset,
    primaryPane,
    initialChartState: chartResetState,
    setInitialChartState,
  } = useChartState(shouldUseSearchParams);
  const [dailyTimeseries, setDailyTimeseries] = useState<TimeSeriesItem[]>();
  const [isLoading, setIsLoading] = useState(false);
  const chartPanes = chartState.chartPanes;

  const chartData = chartPanes.flatMap(panes => panes.chartAssetData);
  const activeAssets = chartData.map(c => c.entity);
  // SearchModal keys its indicator + suggestion fetches off this array's
  // identity, so re-deriving it per render refetches (and blanks) the open
  // modal on every unrelated re-render — e.g. a live price tick in ChartV2View.
  const activeAssetsPerPanels = useMemo(
    () =>
      chartPanes.map(pane =>
        pane.chartAssetData.map(c => ({
          entity: c.entity,
          snakeMeta: c.snakeMeta,
        }))
      ),
    [chartPanes]
  );
  const selectedIndicators = activeAssetsPerPanels
    .flat()
    .map(item => item.snakeMeta?.snake);

  const hasSinglePriceEntity = activeAssets.some(isChartSinglePriceEntity);

  const [horizonRanges] = useState<RangeHorizon[]>(() =>
    Object.values(RangeHorizon)
  );
  const [staleTime] = useState(getTimeTillEndOfDayMs);

  const { chartError } = useChartErrors({ chartData });

  const onChartDataReady = ({
    chartPanes,
    emptyResamples,
    validResample,
    validHorizon,
    dailyTimeseries,
    domain,
    hidden,
  }: OnChartDataReadyProps) => {
    resample.setEmptyResamples(emptyResamples);
    updateChartState({
      resample: validResample,
      horizon: validHorizon,
      chartPanes,
      domain,
      hidden,
    });
    updateChartData(chartPanes, validResample);
    setDailyTimeseries(dailyTimeseries);
  };

  const getTimeSeries = async ({
    latestAsset,
    resolution,
    onFallbackToDaily,
  }: GetTimeSeriesProps) => {
    const result = await queryClient.fetchQuery(
      createChartTimeSeriesQueryOptions({
        latestAsset,
        resolution,
        queryClient,
        staleTime,
      })
    );

    if (result?.fellBackToDaily) {
      onFallbackToDaily?.();
    }

    return result?.data;
  };

  const { changeYAxisSettings, loadChartFromSettings } = useChartSettings({
    ...chartError,
    getTimeSeries,
    onChartDataReady,
    chartPanes,
    updateChartState,
    variants,
  });

  const { handleInitialChartLoad, retryInitialChartLoad } = useChartLoad({
    initialChartSettings,
    initialChartState,
    restoreLastUsedChart,
    openSearch,
    loadChartFromSettings,
    setHasError: chartError.setHasError,
    shouldUseSearchParams,
    setIsLoading,
    updateChartState,
  });

  const {
    changeRange,
    updateChartData,
    onDomainChange,
    disable1DHorizon,
    resample,
  } = useResample({
    ...chartError,
    chartData,
    chartState,
    hasSinglePriceEntity,
    updateChartState,
    getTimeSeries,
    setIsLoading,
  });

  const onAssetOrIndicatorChange = async ({
    entity,
    indicator,
    assetToChange,
  }: {
    indicator: SnakeMeta;
    entity: Entity;
    assetToChange?: Entity;
  }) => {
    chartError.setHasEmpty(false);
    chartError.setHasError(false);

    const asset = {
      ...entity,
      default_snake: indicator.snake,
      currency: indicator.currency,
    };

    if (selectedIndicators.includes(indicator.snake)) {
      return;
    }

    try {
      const isSinglePrice = isChartSinglePriceEntity(asset);
      const nextResample = getAssetResample({
        asset,
        hasSinglePriceEntity: isSinglePrice,
        selectedResample: resample.selectedResample,
      });
      let fellBackToDaily = false;

      const [prices, fetchedChartData] = await Promise.all([
        getTimeSeries({
          latestAsset: asset,
          resolution: nextResample,
          onFallbackToDaily: () => {
            fellBackToDaily = true;
          },
        }),
        resample.fetchResampleIfNeeded(chartData, nextResample),
      ]);
      const appliedResample = getResampleAfterDailyFallback(
        nextResample,
        fellBackToDaily
      );
      const newChartAssetData =
        appliedResample === nextResample
          ? fetchedChartData
          : await resample.fetchResampleIfNeeded(chartData, appliedResample);

      if (!prices?.length) {
        chartError.setAssetWithError(asset);
        chartError.handleEmptyChart(asset.name);
        return;
      }

      const updatedPanes = newChartAssetData.length
        ? updateAllPanes(newChartAssetData, chartPanes)
        : chartPanes;
      const primaryDetails = getTerminalPrimaryDetails(updatedPanes);

      const newPaneIndex = getTargetPaneIndex({
        chartData,
        chartPanes,
        isSinglePrice,
        assetToChange,
        indicator,
        primaryPaneId: primaryDetails.primaryPane?.id,
      });
      const targetPane = chartPanes[newPaneIndex];

      if (chartResetState) {
        const initialPrices = await getTimeSeries({
          latestAsset: asset,
          resolution: chartResetState.resample,
        });

        const targetChartResetPane = chartResetState.chartPanes[newPaneIndex];
        const newResetChartData = createChartDataWithChangedAsset({
          primaryAsset: primaryDetails.primaryAsset,
          chartData: targetChartResetPane?.chartAssetData || [],
          asset: asset,
          snakeMeta: indicator,
          prices: initialPrices || prices,
          assetToChange,
          colors,
          newLineColorToken: getNewLineColorToken({
            indicator,
            chartData,
            colors,
          }),
        });

        if (targetChartResetPane) {
          targetChartResetPane.chartAssetData = newResetChartData;
        } else {
          chartResetState.chartPanes = updatePane(
            chartResetState.chartPanes,
            newPaneIndex,
            getUpdatedPaneData({
              targetPane,
              chartAssetData: newResetChartData,
              isSinglePrice,
            })
          );
        }

        setInitialChartState({
          ...chartResetState,
        });
      }

      const newChartData = createChartDataWithChangedAsset({
        primaryAsset: primaryDetails.primaryAsset,
        chartData: targetPane?.chartAssetData || [],
        asset: asset,
        snakeMeta: indicator,
        prices,
        assetToChange,
        colors,
        newLineColorToken: getNewLineColorToken({
          indicator,
          chartData,
          colors,
        }),
      });

      const newChartPanes = updatePane(
        updatedPanes,
        newPaneIndex,
        getUpdatedPaneData({
          targetPane,
          chartAssetData: newChartData,
          isSinglePrice,
        })
      );

      const baseEmptyResamples = getChartDataEmptyResamples(
        newChartPanes.flatMap(p => p.chartAssetData)
      );
      const emptyResamples = mergeIntradayEmptyResamples(
        baseEmptyResamples,
        fellBackToDaily
      );
      resample.setEmptyResamples(emptyResamples);

      let nextHorizon = initialChartState?.horizon;

      if (fellBackToDaily) {
        nextHorizon = getValidHorizon({
          horizon: nextHorizon ?? chartState.horizon ?? undefined,
          chartData: newChartPanes.flatMap(p => p.chartAssetData),
          emptyResamples,
          hasSinglePriceEntity: isSinglePrice,
          resample: appliedResample,
          fellBackToDaily,
        });
      } else if (
        !nextHorizon &&
        (!chartData.length ||
          (isSinglePrice && isIntradayResample(resample.selectedResample)))
      ) {
        nextHorizon = getHorizons().activeHorizon;
      }

      updateChartData(newChartPanes, appliedResample);
      updateChartState({
        chartPanes: newChartPanes,
        resample: appliedResample,
        ...(nextHorizon && { horizon: nextHorizon }),
      });
    } catch (error) {
      chartError.setHasError(true);
      Tracking.captureException(error);
    }
  };

  const chartPaneActions = useChartPane({
    chartPanes,
    updateChartState,
    resample,
    onAssetOrIndicatorChange,
  });

  const removeActiveAsset = (defaultSnake: string, paneIndex = 0) => {
    const chartAssetPane = chartPanes[paneIndex];
    const filteredData = chartAssetPane.chartAssetData.filter(
      a => a.entity.default_snake !== defaultSnake
    );

    const newChartPanes = updatePane(chartPanes, paneIndex, {
      chartAssetData: filteredData,
      yAxisType:
        filteredData.length === 1 ? 'split' : chartPanes[paneIndex].yAxisType,
    });

    updateChartData(newChartPanes);
    updateChartState({
      chartPanes: newChartPanes,
    });
    resample.setEmptyResamples(getChartDataEmptyResamples(filteredData));
  };

  const hideActiveAsset = async (
    snake: Entity['default_snake'],
    {
      isHidden = false,
      paneIndex = 0,
      seriesType,
    }: {
      isHidden?: boolean;
      paneIndex?: number;
      /**
       * Applied in the same write as the reveal. Revealing an asset into a
       * different type via `changeSeriesType` would rebuild `chartPanes`
       * from the pre-reveal snapshot and drop `isHidden`.
       */
      seriesType?: SeriesType;
    }
  ) => {
    const updatedData = chartPanes[paneIndex].chartAssetData.map(a => {
      if (a.entity.default_snake === snake) {
        return { ...a, isHidden, ...(seriesType && { seriesType }) };
      }

      return a;
    });

    const hidden = updatedData
      .filter(item => item.isHidden)
      .map(item => item.entity.default_snake);
    const newChartPanes = updateChartPane(chartPanes, updatedData, paneIndex);
    updateChartData(newChartPanes);
    updateChartState({
      chartPanes: newChartPanes,
      hidden: hidden.length ? hidden : undefined,
    });
  };

  const changeChartLineColor = (
    colorId: number,
    { snake, paneIndex }: { snake?: string; paneIndex: number }
  ) => {
    const updatedChartPanes = chartPanes[paneIndex].chartAssetData.map(a => {
      if (a.entity.default_snake === snake) {
        return { ...a, lineColorToken: colors.getMulti()[colorId].token };
      }

      return a;
    });

    const newChartPanes = updateChartPane(
      chartPanes,
      updatedChartPanes,
      paneIndex
    );
    updateChartData(newChartPanes);
    updateChartState({ chartPanes: newChartPanes });
  };

  const changeSeriesType = (
    seriesType: SeriesType,
    { snake, paneIndex = 0 }: { snake?: string; paneIndex?: number }
  ) => {
    const updatedChartPane: TerminalPaneData = snake
      ? {
          ...chartPanes[paneIndex],
          chartAssetData: chartPanes[paneIndex].chartAssetData.map(a => {
            if (a.entity.default_snake === snake) {
              return { ...a, seriesType };
            }

            return a;
          }),
        }
      : { ...chartPanes[0], seriesType };

    updateChartState({
      chartPanes: [
        ...chartPanes.slice(0, paneIndex),
        updatedChartPane,
        ...chartPanes.slice(paneIndex + 1),
      ],
    });
  };

  /** One write for ChartV2's global chart-type chip (all assets / panes). */
  const changeAllSeriesTypes = (seriesType: SeriesType) => {
    updateChartState({
      chartPanes: chartPanes.map(pane => ({
        ...pane,
        seriesType,
        chartAssetData: pane.chartAssetData.map(a => ({ ...a, seriesType })),
      })),
    });
  };

  return {
    ...chartError,
    ...resample,
    chartPaneActions,
    chartData,
    activeAssetsPerPanels,
    removeActiveAsset,
    hideActiveAsset,
    changeRange,
    changeChartLineColor,
    horizonRanges,
    changeSeriesType,
    changeAllSeriesTypes,
    onAssetOrIndicatorChange,
    hasSinglePriceEntity,
    disable1DHorizon,
    onDomainChange,
    changeYAxisSettings,
    dailyTimeseries,
    chartState,
    handleInitialChartLoad,
    retryInitialChartLoad,
    chartIsLoading: isLoading,
    resetChartState,
    loadChartFromSettings,
    primaryPane,
    primaryAsset,
    chartResetState,
    updateChartState,
    setInitialChartState,
  };
};
